import { emptyProseMirrorContent } from "@shared/lib/card-stack/prose-mirror-utils"
import type { FindFn } from "@shared/models/analytics/base"
import { LessonEditCard } from "@shared/models/analytics/lesson"
import type { ProseMirrorContent } from "@shared/models/card-stack"
import type { PatchModel } from "@shared/models/patch"
import { partial } from "lodash-es"
import type { PreparedAction } from "../../action-doc-builder"
import type { Narrative } from "../create-narrative"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"
import { lessonAnalyticsCore } from "./lesson-analytics-core"
import { unifiedContentPatch } from "./unified-content-patch"

export interface Payload {
  cardStackId: string
  planbookId: string
  courseId: string | null
  dateString: string | null
  cardId: string
  proseMirror: ProseMirrorContent
}

export function prepareCreateUnifiedNote(payload: Payload): PreparedAction {
  return {
    name: "LESSON_CREATE_UNIFIED_NOTE",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  return [unifiedContentPatch(payload.cardStackId, payload.proseMirror).patch]
}

function undoPatches(payload: Payload): Array<PatchModel> {
  // Restore the empty slot - though it'll still have a course-date-custom from the first edit action.
  return [unifiedContentPatch(payload.cardStackId, emptyProseMirrorContent()).patch]
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
  const models = defaultLessonModels(payload, findFn, userId)
  const core = lessonAnalyticsCore(payload.cardStackId, payload.planbookId, models, findFn, userId)

  const courseTitle = models.course?.attributes.title
  const courseTitlePhrase = courseTitle ? courseTitle + " class" : "class"
  const dateString = models.dateStringForCardStack

  return {
    title: "Added Note",
    titleForAnalytics: "Lesson - Add Note",
    description: dateString
      ? `Added a note to your ${courseTitlePhrase} on ${dateString}.`
      : `Added a note to your ${courseTitlePhrase}.`,
    context: {
      ...defaultLessonContext(payload, models),
      isNonInstructional: true,
      cardId: payload.cardId,
      cardType: "card-value-html",
    },
    newAnalyticsEvents: [
      LessonEditCard.create(core, { verb: "adding", specifier: null, cardId: payload.cardId }),
    ],
  }
}
